import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@jem-open/jem-ui';
import { getBatchItems } from './services/savingsBatchService';
import { PAID_STATUSES, statusVariant } from './transactionStatus';
import type {
  SavingPaymentBatch,
  SavingPaymentBatchItem,
  TransactionStatus,
} from './types';

interface SavingsBatchItemsModalProps {
  batch: SavingPaymentBatch | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatCurrency = (amount: string): string => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return `R${n.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const SavingsBatchItemsModal = ({
  batch,
  open,
  onOpenChange,
}: SavingsBatchItemsModalProps) => {
  const [items, setItems] = useState<SavingPaymentBatchItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !batch) return;
    let cancelled = false;
    setLoading(true);
    getBatchItems(batch.id)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, batch]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {batch ? `Batch #${batch.id} — ${batch.employerName}` : 'Batch'}
          </DialogTitle>
          {batch && (
            <DialogDescription>
              {batch.saversCount} employees ·{' '}
              {formatCurrency(batch.totalAmount)}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No items in this batch.
            </p>
          ) : (
            (() => {
              const sumOf = (
                status: TransactionStatus | TransactionStatus[]
              ) => {
                const set = Array.isArray(status) ? status : [status];
                const matched = items.filter((i) => set.includes(i.status));
                return {
                  count: matched.length,
                  total: matched.reduce(
                    (s, i) => s + (Number(i.amount) || 0),
                    0
                  ),
                };
              };
              const paid = sumOf(PAID_STATUSES);
              const skipped = sumOf('SKIPPED');
              const failed = sumOf('FAILED');
              const pending = sumOf('PENDING');
              return (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Franc Goal</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.employeeName}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {item.francGoalIdentifier}
                        </TableCell>
                        <TableCell>
                          <Tag variant={statusVariant(item.status)}>
                            {item.status}
                          </Tag>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3} className="font-semibold">
                        Paid ({paid.count})
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(paid.total.toFixed(2))}
                      </TableCell>
                    </TableRow>
                    {pending.count > 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="font-semibold">
                          Pending ({pending.count})
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(pending.total.toFixed(2))}
                        </TableCell>
                      </TableRow>
                    )}
                    {skipped.count > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="font-semibold text-greyscale-text-caption">
                          Skipped ({skipped.count})
                        </TableCell>
                        <TableCell className="text-right font-semibold text-greyscale-text-caption">
                          {formatCurrency(skipped.total.toFixed(2))}
                        </TableCell>
                      </TableRow>
                    )}
                    {failed.count > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="font-semibold text-red-600">
                          Failed ({failed.count})
                        </TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {formatCurrency(failed.total.toFixed(2))}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableFooter>
                </Table>
              );
            })()
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SavingsBatchItemsModal;
